import React, {
  Fragment,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import ReactDOM from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from '@asu/components-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuildingColumns,
  faLocationDot,
  faXmark,
  faList,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import { useQuery } from '@tanstack/react-query';
import useServiceAuth from 'auth/useServiceAuth';
import {
  checksheetGet,
  checksheetGetAllByYear,
  checksheetGetAllProposedByYear,
  checksheetUpdate,
} from 'apis/checksheetAPIs';
import { fetchDplUndergradMinorsCertificates } from 'apis/dplAPIs';
import DOMPurify from 'dompurify';
import CourseListDetails from './CourseListDetails';
import StaticData from './StaticData';
import { classNames } from 'utils';
import { v4 as uuidv4 } from 'uuid';
import {
  setCourseLists,
  setDetails,
  setChecksheetRequirementList,
} from 'state/slices/checksheetSlice';

const SearchProgram = ({ program }) => {
  const { degree, owners, campusesOffered } = useMemo(() => {
    const degree = program.degreeDescriptionShort
      ? program.degreeDescriptionShort === 'CERT'
        ? ', Certificate'
        : `, ${program.degreeDescriptionShort}`
      : program.acadPlanType === 'MIN'
        ? ', Minor'
        : null;

    // Use Set for O(1) deduplication
    const ownerSet = new Set();
    const owners = [];
    program.owners.forEach((programOwner) => {
      if (!ownerSet.has(programOwner.collegeAcadOrg)) {
        ownerSet.add(programOwner.collegeAcadOrg);
        owners.push({
          collegeAcadOrg: programOwner.collegeAcadOrg,
          collegeDescription: programOwner.collegeDescription,
        });
      }
    });

    const campusesOffered = [];
    if (program.campusesOffered && program.campusesOffered.length > 0) {
      program.campusesOffered.forEach((campus) => {
        const obj = { ...campus };
        const campusFound = StaticData.campusUndergradList.find(
          (campusUndergrad) => campusUndergrad.value === campus.campusCode
        );
        if (campusFound?.label) obj.label = campusFound.label;
        campusesOffered.push(obj);
      });
    }

    return { degree, owners, campusesOffered };
  }, [program]);

  return (
    <>
      <div className="d-flex gap-2 justify-content-between">
        <div className="fw-bold" style={{ fontSize: '18px' }}>
          {program.acadPlanDescription}
          {degree}
        </div>
        <div style={{ marginTop: '3px' }}>{program.acadPlanCode}</div>
      </div>
      {program.subplan && (
        <div className="d-flex gap-2 justify-content-between">
          <div className="fw-bold">
            {program.subplan.description}
            <span className="subplan-tag ms-1">Subplan</span>
          </div>
          <div style={{ marginTop: '3px' }}>
            {program.subplan.acadSubPlanCode}
          </div>
        </div>
      )}
      <div className="d-flex gap-1 align-items-center">
        <FontAwesomeIcon
          icon={faBuildingColumns}
          className="text-gray-6"
          width={'16px'}
        />
        {owners.map((owner, index, array) => (
          <Fragment key={owner.collegeAcadOrg}>
            {owner.collegeDescription}
            {index !== array.length - 1 && ', '}
          </Fragment>
        ))}
      </div>
      <div className="d-flex gap-1 align-items-center">
        <FontAwesomeIcon
          icon={faLocationDot}
          className="text-gray-6"
          width={'16px'}
        />
        {campusesOffered.map((campus, index, array) => (
          <Fragment key={campus.campusCode}>
            {campus.label}
            {index !== array.length - 1 && ', '}
          </Fragment>
        ))}
      </div>
    </>
  );
};

const RequirementDisplay = ({
  requirement,
  courseLists,
  isSelected,
  onToggle,
  isTemplate = false,
  templatesMatch = false,
}) => {
  if (!requirement.courses || requirement.courses.length === 0) {
    return null;
  }

  // Check if this requirement contains template text
  // Template text is importable if templates match
  const hasTemplateText = requirement.courses.some(
    (course) =>
      course.itemType === 'req_text' && course.groupIndex !== 'custom_text'
  );
  const isNonSelectable = isTemplate || (hasTemplateText && !templatesMatch);

  const CourseListAccordion = ({ course }) => {
    const [showCourseList, setShowCourseList] = useState(false);

    const handleShowCourseList = (e) => {
      e.stopPropagation();
      setShowCourseList(!showCourseList);
    };

    return (
      <>
        <button
          type="button"
          className="button-link"
          onClick={(e) => handleShowCourseList(e)}
          style={{
            border: 'none',
            background: 'none',
            padding: 0,
            font: 'inherit',
            cursor: 'pointer',
          }}
        >
          <FontAwesomeIcon
            icon={faChevronDown}
            className={classNames(showCourseList && 'rotate-180')}
            style={{ margin: '0 4px' }}
          />
          {showCourseList ? 'Hide course list' : 'Show course list'}
        </button>
        {showCourseList && (
          <CourseListDetails
            courseListName={courseLists[course.courseListId]?.courseListName}
            courses={courseLists[course.courseListId]?.courses || []}
            edit={false}
            division={course.division}
          />
        )}
      </>
    );
  };

  return (
    <div
      className={classNames(
        'd-flex py-2 border border-top-0 border-gray-3',
        isNonSelectable ? 'bg-gray-2 text-gray-5' : 'bg-white'
      )}
      style={{ cursor: isNonSelectable ? 'default' : 'auto' }}
    >
      <div className="flex-shrink-0 pt-0 px-2" style={{ width: '5%' }}>
        {!isNonSelectable && (
          <input
            type="checkbox"
            className="requirement-row-checkbox"
            id={`checkbox-requirement-${requirement.requirementId}`}
            checked={isSelected}
            onChange={() => onToggle(requirement.requirementId)}
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </div>
      <div className="d-flex gap-1 pe-2" style={{ width: '65%' }}>
        <div style={{ width: '94%' }}>
          {requirement.courses.map((course, index) => (
            <Fragment key={index}>
              <span className="requirement">
                {course.connector && (
                  <>
                    {course.connector !== 'or' &&
                      index !== 0 &&
                      requirement.courses[index - 1]?.groupIndex !==
                        'custom_text' && <br />}
                    {course.connector === 'or' && ' '}
                    <span
                      className={classNames(
                        course.connector,
                        'fw-bold rounded-1 text-nowrap'
                      )}
                    >
                      {course.connector}
                    </span>
                  </>
                )}
                <span>
                  {course.itemType === 'req_check' &&
                  course.checkType === 'gpa' ? (
                    <>
                      <span className="fw-bold">Check</span>:{' '}
                    </>
                  ) : (
                    course.checkType === 'milestone' && (
                      <>
                        <span className="fw-bold">Milestone</span>:{' '}
                      </>
                    )
                  )}
                  {course.gpa && `Minimum ${course.gpa} `}
                  {course.checkSubtype === 'asu_cum_gpa' &&
                    'ASU Cumulative GPA'}
                  {course.checkSubtype === 'major_gpa' && 'Major GPA'}
                  {course.subject && (
                    <span
                      className={classNames(
                        course.itemType !== 'req_check' && 'fw-bold'
                      )}
                    >
                      {course.subject}{' '}
                    </span>
                  )}
                  {course.number && (
                    <>
                      <span className="fw-bold">{course.number}</span>{' '}
                    </>
                  )}
                  {course.proposedTitle ? (
                    <span>
                      {course.proposedTitle}
                      <span className="text-warning-custom bg-warning-custom fw-bold mx-1 px-1">
                        Proposed
                      </span>
                    </span>
                  ) : (
                    course.description
                  )}
                  {!!course.division && course.division.trim()
                    ? (course.division === 'Upper' ||
                      course.division === 'Lower'
                        ? course.division + ' Division'
                        : course.division + '-Level') + ' '
                    : ''}
                  {course.itemType === 'req_text' ? (
                    course.groupIndex === 'custom_text' ? (
                      // Custom text (no group)
                      course.customText && (
                        <span
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(course.customText),
                          }}
                        />
                      )
                    ) : (
                      // Template text (group: text)
                      <>
                        {course.group && (
                          <span className="fw-bold">{course.group}: </span>
                        )}
                        {course.text}
                      </>
                    )
                  ) : (
                    <>{course.group && course.group + ': '}</>
                  )}
                  {course.gs && course.subject && <span> ({course.gs})</span>}
                  {course.itemType === 'req_gs' && !!course.gs && (
                    <>
                      {StaticData.gsList.find((gs) => gs.value === course.gs)
                        ?.label || course.gs}{' '}
                      ({course.gs})
                    </>
                  )}
                  {(course.itemType === 'req_elective' ||
                    course.itemType === 'req_subject') &&
                    'Elective'}
                  {course.itemType === 'req_course_list' &&
                    course.courseListId &&
                    courseLists?.[course.courseListId] && (
                      <span className="fw-bold underline-hover mx-1">
                        <FontAwesomeIcon
                          icon={faList}
                          style={{ marginRight: '4px' }}
                        />
                        {courseLists[course.courseListId]?.courseListName ||
                          'Untitled course list'}
                      </span>
                    )}
                </span>
              </span>
              {course.itemType === 'req_course_list' &&
                course.courseListId &&
                courseLists?.[course.courseListId] && (
                  <CourseListAccordion course={course} />
                )}
            </Fragment>
          ))}
        </div>
      </div>
      <div
        className={classNames(
          (!requirement.grade || !requirement.grade.trim()) && 'text-gray-4',
          'text-nowrap text-end px-2'
        )}
        style={{
          width: '15%',
          fontSize: '14px',
        }}
      >
        {requirement.grade && requirement.grade.trim()
          ? requirement.grade
          : '\u2014'}
      </div>
      <div className="d-flex justify-content-end px-2" style={{ width: '15%' }}>
        <div
          className={classNames(
            !!requirement.hours
              ? 'bg-gray-2 fw-semibold rounded-pill'
              : 'text-gray-4',
            'text-nowrap text-end lh-1'
          )}
          style={{
            fontSize: '14px',
            height: 'fit-content',
            padding: '5px 8px',
          }}
        >
          {!!requirement.hours ? `${requirement.hours}` : '\u2014'}
          {!!requirement.maxHours &&
            requirement.maxHours !== 0 &&
            requirement.maxHours !== requirement.hours &&
            `${' - '} ${requirement.maxHours}`}
        </div>
      </div>
    </div>
  );
};

const ChecksheetRequirementsDisplay = ({
  checksheetData,
  selectedRequirements,
  setSelectedRequirements,
  selectedSubsections,
  setSelectedSubsections,
  templatesMatch = false,
  willRequirementImport,
}) => {
  const details = useMemo(() => {
    if (!checksheetData?.details) return null;
    try {
      return JSON.parse(checksheetData.details);
    } catch (error) {
      console.error('Error parsing checksheet details:', error);
      return null;
    }
  }, [checksheetData]);

  // Parse templateObject to get component names
  const componentNames = useMemo(() => {
    if (!checksheetData?.templateObject?.componentObjects) return {};

    const names = {};
    Object.entries(checksheetData.templateObject.componentObjects).forEach(
      ([componentId, componentObj]) => {
        try {
          const componentDetails = JSON.parse(componentObj.details);
          names[componentId] = componentDetails.componentName || '';
        } catch (error) {
          console.error('Error parsing component details:', error);
        }
      }
    );
    return names;
  }, [checksheetData]);

  // Parse templateObject to get subsection names from template
  const templateSubsectionNames = useMemo(() => {
    if (!checksheetData?.templateObject?.componentObjects) return {};

    const names = {}; // key: "componentId:subsectionId" -> subsectionName
    Object.entries(checksheetData.templateObject.componentObjects).forEach(
      ([componentId, componentObj]) => {
        try {
          const componentDetails = JSON.parse(componentObj.details);
          if (componentDetails.subsections) {
            Object.entries(componentDetails.subsections).forEach(
              ([subsectionId, subsection]) => {
                const key = `${componentId}:${subsectionId}`;
                names[key] = subsection.subsectionName || '';
              }
            );
          }
        } catch (error) {
          console.error('Error parsing component details:', error);
        }
      }
    );
    return names;
  }, [checksheetData]);

  // Parse templateObject to get template requirements
  const templateRequirements = useMemo(() => {
    if (!checksheetData?.templateObject?.componentObjects) return {};

    const reqsBySubsection = {}; // key: "componentId:subsectionId" -> [requirements]
    Object.entries(checksheetData.templateObject.componentObjects).forEach(
      ([componentId, componentObj]) => {
        try {
          const componentDetails = JSON.parse(componentObj.details);
          if (componentDetails.subsections) {
            Object.entries(componentDetails.subsections).forEach(
              ([subsectionId, subsection]) => {
                const key = `${componentId}:${subsectionId}`;
                if (
                  subsection.requirements &&
                  subsection.requirements.length > 0
                ) {
                  reqsBySubsection[key] = subsection.requirements;
                }
              }
            );
          }
        } catch (error) {
          console.error('Error parsing component details:', error);
        }
      }
    );
    return reqsBySubsection;
  }, [checksheetData]);

  // Get all template subsections (including empty ones) for merging
  const templateSubsections = useMemo(() => {
    if (!checksheetData?.templateObject?.componentObjects) return {};

    const subsectionsByComponent = {}; // key: componentId -> { subsectionId: subsection }
    Object.entries(checksheetData.templateObject.componentObjects).forEach(
      ([componentId, componentObj]) => {
        try {
          const componentDetails = JSON.parse(componentObj.details);
          if (componentDetails.subsections) {
            subsectionsByComponent[componentId] = componentDetails.subsections;
          }
        } catch (error) {
          console.error('Error parsing component details:', error);
        }
      }
    );
    return subsectionsByComponent;
  }, [checksheetData]);

  // Merge components from checksheet and template, preserving template order
  const componentsToDisplay = useMemo(() => {
    // Get component order from template (canonical order), then add checksheet-only components
    const templateComponentIds = Object.keys(templateSubsections);
    const checksheetOnlyIds = details?.componentRequirements
      ? Object.keys(details.componentRequirements).filter(
          (id) => id !== 'orphanSection' && !templateSubsections[id]
        )
      : [];

    const allComponentIds = [...templateComponentIds, ...checksheetOnlyIds];

    return allComponentIds.map((componentId) => {
      const checksheetComponent = details?.componentRequirements?.[componentId];
      const templateComponent = templateSubsections[componentId];

      // Merge subsections from both sources, preserving template order
      const mergedSubsections = {};

      // Add template subsections first (preserving their order)
      if (templateComponent) {
        Object.entries(templateComponent).forEach(
          ([subsectionId, templateSubsection]) => {
            // Check if this subsection exists in checksheet
            const checksheetSubsection =
              checksheetComponent?.subsections?.[subsectionId];

            if (checksheetSubsection) {
              // Use checksheet data (which includes requirements)
              mergedSubsections[subsectionId] = checksheetSubsection;
            } else {
              // Template-only subsection - create a minimal object
              mergedSubsections[subsectionId] = {
                requirements: [],
                subsectionName: null, // Will be filled from templateSubsectionNames
              };
            }
          }
        );
      }

      // Add checksheet subsections that don't exist in template
      if (checksheetComponent?.subsections) {
        Object.entries(checksheetComponent.subsections).forEach(
          ([subsectionId, subsection]) => {
            if (!mergedSubsections[subsectionId]) {
              mergedSubsections[subsectionId] = subsection;
            }
          }
        );
      }

      return [
        componentId,
        { ...checksheetComponent, subsections: mergedSubsections },
      ];
    });
  }, [details, templateSubsections]);

  if (!details || !details.componentRequirements) {
    return null;
  }

  const courseLists = details.courseLists || {};

  const handleToggleRequirement = (requirementId) => {
    setSelectedRequirements((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(requirementId)) {
        newSet.delete(requirementId);
      } else {
        newSet.add(requirementId);
      }
      return newSet;
    });
  };

  const handleToggleAll = (subsectionRequirements) => {
    const requirementIds = subsectionRequirements.map(
      (req) => req.requirementId
    );
    const allSelected = requirementIds.every((id) =>
      selectedRequirements.has(id)
    );

    setSelectedRequirements((prev) => {
      const newSet = new Set(prev);
      if (allSelected) {
        requirementIds.forEach((id) => newSet.delete(id));
      } else {
        requirementIds.forEach((id) => newSet.add(id));
      }
      return newSet;
    });
  };

  const handleToggleSubsection = (componentId, subsectionId) => {
    const key = `${componentId}:${subsectionId}`;
    setSelectedSubsections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  return (
    <div className="mb-4">
      <div>
        {componentsToDisplay.map(([componentId, component]) => (
          <div key={componentId} className="bg-gray-1 p-3 mb-12">
            {/* Display component name from template if available */}
            {componentNames[componentId] && (
              <div className="fw-bold fs-4 mb-2">
                {componentNames[componentId]}
              </div>
            )}
            {component.subsections &&
              Object.entries(component.subsections).map(
                ([subsectionId, subsection], index, array) => {
                  const subsectionKey = `${componentId}:${subsectionId}`;
                  // Get subsection name from checksheet or fall back to template
                  const subsectionName =
                    subsection.subsectionName ||
                    templateSubsectionNames[subsectionKey] ||
                    '';
                  // Only allow selection if subsection name exists in checksheet (not just template)
                  const isSelectableSubsection = !!subsection.subsectionName;

                  return (
                    <div
                      key={subsectionId}
                      className={classNames(
                        array.length - 1 !== index && 'mb-6'
                      )}
                    >
                      {subsectionName && (
                        <div className="d-flex align-items-center gap-2 mb-2">
                          {isSelectableSubsection && (
                            <input
                              type="checkbox"
                              className="requirement-row-checkbox"
                              id={`checkbox-subsection-${componentId}-${subsectionId}`}
                              checked={selectedSubsections.has(subsectionKey)}
                              onChange={() =>
                                handleToggleSubsection(
                                  componentId,
                                  subsectionId
                                )
                              }
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                          {!isSelectableSubsection && (
                            <div
                              style={{ width: '16px', marginLeft: '2px' }}
                            ></div>
                          )}
                          <div>
                            <label
                              htmlFor={
                                isSelectableSubsection
                                  ? `checkbox-subsection-${componentId}-${subsectionId}`
                                  : undefined
                              }
                              className={classNames(
                                'fw-bold fs-5 mb-0',
                                !isSelectableSubsection && 'text-gray-5'
                              )}
                              style={{
                                cursor: isSelectableSubsection
                                  ? 'pointer'
                                  : 'default',
                              }}
                            >
                              {subsectionName}
                            </label>
                            {isSelectableSubsection &&
                              selectedSubsections.has(subsectionKey) && (
                                <div
                                  className="text-gray-6"
                                  style={{ fontSize: '14px' }}
                                >
                                  Create subsection with this name
                                </div>
                              )}
                          </div>
                        </div>
                      )}
                      {(subsection.requirements ||
                        templateRequirements[subsectionKey]) && (
                        <div>
                          {(() => {
                            // Get checksheet requirements
                            const checksheetReqs =
                              subsection.requirements || [];
                            const checksheetReqMap = new Map(
                              checksheetReqs.map((r) => [r.requirementId, r])
                            );

                            // Get template requirements
                            const templateReqs =
                              templateRequirements[subsectionKey] || [];

                            // Merge: Follow template order, then add checksheet-only requirements
                            // - If requirement exists in both, use checksheet version (selectable)
                            // - If requirement only in template, use template version (non-selectable)
                            // - If requirement only in checksheet, add at end (selectable)
                            const templateReqIds = new Set(
                              templateReqs.map((r) => r.requirementId)
                            );

                            const mergedRequirements = [
                              // First, all template requirements (in template order)
                              ...templateReqs.map((req) => {
                                const checksheetVersion = checksheetReqMap.get(
                                  req.requirementId
                                );
                                if (checksheetVersion) {
                                  // Exists in both - use checksheet version, mark as selectable
                                  return {
                                    ...checksheetVersion,
                                    isTemplate: false,
                                  };
                                } else {
                                  // Template-only - non-selectable
                                  return { ...req, isTemplate: true };
                                }
                              }),
                              // Then, checksheet requirements not in template
                              ...checksheetReqs
                                .filter(
                                  (req) =>
                                    !templateReqIds.has(req.requirementId)
                                )
                                .map((req) => ({ ...req, isTemplate: false })),
                            ];

                            // Filter to only truly importable requirements
                            // Use the same logic as the import function to determine selectability
                            const selectableRequirements =
                              mergedRequirements.filter(
                                (r) =>
                                  !r.isTemplate && // Template requirements never selectable
                                  willRequirementImport(r, templatesMatch) // Requirement has importable content
                              );

                            return (
                              <>
                                <div className="d-flex py-2">
                                  <div
                                    className="flex-shrink-0 px-2"
                                    style={{ width: '5%' }}
                                  >
                                    {selectableRequirements.length > 0 && (
                                      <input
                                        type="checkbox"
                                        className="requirement-row-checkbox"
                                        checked={selectableRequirements.every(
                                          (req) =>
                                            selectedRequirements.has(
                                              req.requirementId
                                            )
                                        )}
                                        onChange={() =>
                                          handleToggleAll(
                                            selectableRequirements
                                          )
                                        }
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    )}
                                  </div>
                                  <div
                                    className={classNames(
                                      'fw-bold pe-2',
                                      selectableRequirements.length === 0 &&
                                        'text-gray-5'
                                    )}
                                    style={{ width: '65%' }}
                                  >
                                    Requirement
                                  </div>
                                  <div
                                    className={classNames(
                                      'fw-bold text-nowrap text-end px-2',
                                      selectableRequirements.length === 0 &&
                                        'text-gray-5'
                                    )}
                                    style={{ width: '15%' }}
                                  >
                                    Min grade
                                  </div>
                                  <div
                                    className={classNames(
                                      'fw-bold text-nowrap text-end px-2',
                                      selectableRequirements.length === 0 &&
                                        'text-gray-5'
                                    )}
                                    style={{ width: '15%' }}
                                  >
                                    Credit hours
                                  </div>
                                </div>
                                <div
                                  className={classNames(
                                    'border-top',
                                    selectableRequirements.length === 0
                                      ? 'border-gray-5'
                                      : 'border-gray-7'
                                  )}
                                >
                                  {mergedRequirements.map((req, index) => (
                                    <RequirementDisplay
                                      key={req.requirementId || index}
                                      requirement={req}
                                      courseLists={courseLists}
                                      isSelected={selectedRequirements.has(
                                        req.requirementId
                                      )}
                                      onToggle={handleToggleRequirement}
                                      isTemplate={req.isTemplate}
                                      templatesMatch={templatesMatch}
                                    />
                                  ))}
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  );
                }
              )}
          </div>
        ))}
      </div>

      {/* Raw checksheet data for reference */}
      {/* <div className="mt-4">
        <div className="fw-bold mb-2">Raw Checksheet Data (for reference):</div>
        <div
          className="border border-gray-3 p-3 bg-gray-1"
          style={{ maxHeight: '400px', overflow: 'auto' }}
        >
          <pre style={{ margin: 0, fontSize: '12px' }}>
            {JSON.stringify(checksheetData, null, 2)}
          </pre>
        </div>
      </div> */}
    </div>
  );
};

// Helper: Check if a requirement will be filtered out during import
// A requirement is filtered out if ALL its courses are non-importable template text
const willRequirementImport = (requirement, templatesMatch) => {
  const importableCourses = requirement.courses?.filter((course) => {
    if (course.itemType === 'req_text') {
      // Custom text is always importable
      if (course.groupIndex === 'custom_text') {
        return true;
      }
      // Template text is importable only if templates match
      return templatesMatch;
    }
    return true; // All non-text courses are importable
  });

  // Requirement imports if at least one course remains after filtering
  return importableCourses && importableCourses.length > 0;
};

const ImportForm = ({
  handleClose,
  programList,
  checksheetList,
  proposedList,
  currentYear,
  checksheetId,
  componentId,
  subsectionId,
  currentRequirements,
  courseLists,
  updateData,
}) => {
  const { getAccessToken } = useServiceAuth();
  const dispatch = useDispatch();

  const [catalogYear, setCatalogYear] = useState(currentYear || '');
  const [searchString, setSearchString] = useState('');
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedProgramDegree, setSelectedProgramDegree] = useState(null);
  const [selectedChecksheet, setSelectedChecksheet] = useState(null);
  const [selectedChecksheetId, setSelectedChecksheetId] = useState(null);
  const [selectedRequirements, setSelectedRequirements] = useState(new Set());
  const [selectedSubsections, setSelectedSubsections] = useState(new Set()); // Track selected subsections by "componentId:subsectionId"

  const searchInputRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);

  const collegeList = useSelector((state) => state.dataLists.collegeList);

  // Get checksheet details object from Redux (contains all checksheet data)
  const details = useSelector((state) => state.checksheet.details);

  // Fetch programs with React Query for automatic refetching when stale
  const programsQuery = useQuery({
    queryKey: ['allPrograms'],
    queryFn: fetchDplUndergradMinorsCertificates,
    staleTime: 5 * 60 * 1000, // 5 minutes - don't refetch if data is fresh
    placeholderData: programList?.length > 0 ? programList : undefined, // Show while loading, but don't block fetch
    refetchOnMount: true, // Always check for fresh data when component mounts
    select: (rawData) => {
      // If data is already processed (from initialData/props), return as-is
      if (!rawData || rawData.length === 0) {
        return rawData;
      }
      if (
        rawData[0]?.acadPlanDescription &&
        !rawData[0]?.acadPlanMarketingDescription
      ) {
        return rawData; // Already processed
      }

      // Process raw DPL API data
      const allPrograms = [];
      rawData.forEach((program) => {
        const obj = {
          acadPlanCode: program.acadPlanCode,
          acadPlanDescription:
            program.acadPlanMarketingDescription || program.acadPlanDescription,
          degreeDescriptionShort: program.degreeDescriptionShort,
          campusesOffered: program.campusesOffered,
          acadPlanType: program.acadPlanType,
          owners: program.owners,
          subplan: null,
        };

        allPrograms.push(obj);

        // Add subplans
        if (program.subplans) {
          program.subplans.forEach((subplan) => {
            const subplanObj = {
              acadPlanCode: program.acadPlanCode,
              acadPlanDescription:
                program.acadPlanMarketingDescription ||
                program.acadPlanDescription,
              degreeDescriptionShort: program.degreeDescriptionShort,
              acadPlanType: program.acadPlanType,
              campusesOffered: program.campusesOffered,
              owners: program.owners,
              subplan: subplan,
            };

            allPrograms.push(subplanObj);
          });
        }
      });

      return allPrograms;
    },
  });

  // Fetch checksheets for the selected catalog year
  const checksheetsQuery = useQuery({
    queryKey: ['checksheets', catalogYear],
    queryFn: async () => {
      const token = await getAccessToken();
      return checksheetGetAllByYear({ year: catalogYear, token });
    },
    enabled: !!catalogYear,
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: checksheetList?.length > 0 ? checksheetList : undefined,
    refetchOnMount: true,
    select: (rawData) => {
      // If data is already processed (from initialData/props), return as-is
      if (!rawData || rawData.length === 0) {
        return rawData;
      }
      if (rawData[0]?.id) {
        return rawData; // Already processed
      }
      // Process raw DynamoDB data
      return rawData.map((checksheet) => {
        const idYear = checksheet['sk']?.S?.split('*')[0];
        let lockedBy = {};

        if (checksheet['lockedBy']?.S) {
          lockedBy = JSON.parse(checksheet['lockedBy'].S);
        }

        let details = {};
        if (checksheet['details']?.S) {
          details = JSON.parse(checksheet['details'].S);
        }

        return {
          id: checksheet['sk']?.S || '',
          programType: checksheet['programType']?.S || '',
          checksheetObj: checksheet,
          catalogYear: idYear,
          status: checksheet['progress']?.S ?? null,
          updatedBy: checksheet['updatedBy']?.S,
          updatedOn: checksheet['updatedOn']?.S,
          lockedBy: lockedBy,
          details: details,
          proposed: checksheet['proposed'] ? true : false,
        };
      });
    },
  });

  // Fetch proposed checksheets for the selected catalog year
  const proposedQuery = useQuery({
    queryKey: ['proposedChecksheets', catalogYear],
    queryFn: async () => {
      const token = await getAccessToken();
      return checksheetGetAllProposedByYear({ year: catalogYear, token });
    },
    enabled: !!catalogYear,
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: proposedList?.length > 0 ? proposedList : undefined,
    refetchOnMount: true,
    select: (rawData) => {
      // If data is already processed (from initialData/props), return as-is
      if (!rawData || rawData.length === 0) return rawData;
      if (rawData[0]?.id) return rawData; // Already processed

      // Process raw DynamoDB data for proposed checksheets
      return rawData.map((checksheet) => {
        const idYear = checksheet['sk']?.S?.split('*')[0];
        let lockedBy = {};

        if (checksheet['lockedBy']?.S) {
          lockedBy = JSON.parse(checksheet['lockedBy'].S);
        }

        return {
          id: checksheet['sk']?.S || '',
          checksheetName: checksheet['checksheetName'] || '',
          programType: checksheet['programType']?.S || '',
          checksheetObj: checksheet,
          catalogYear: idYear,
          status: checksheet['progress']?.S ?? null,
          updatedBy: checksheet['updatedBy']?.S,
          updatedOn: checksheet['updatedOn']?.S,
          templateId: checksheet['templateId']?.S ?? null,
          lockedBy: lockedBy,
          proposed: checksheet['proposed'] ? true : false,
        };
      });
    },
  });

  // Use query data with fallback to props
  const activeProgramList = useMemo(
    () => programsQuery.data || programList || [],
    [programsQuery.data, programList]
  );
  const activeChecksheetList = useMemo(
    () => checksheetsQuery.data || checksheetList || [],
    [checksheetsQuery.data, checksheetList]
  );
  const activeProposedList = useMemo(
    () => proposedQuery.data || proposedList || [],
    [proposedQuery.data, proposedList]
  );

  // Create lookup maps for O(1) access
  const checksheetLookup = useMemo(() => {
    const lookup = new Map();
    (activeChecksheetList || [])
      .filter((checksheet) => {
        if (!catalogYear) return true;
        const [year] = checksheet.id.split('*');
        return year === catalogYear;
      })
      .forEach((checksheet) => {
        const [, , planCode, subPlanCode] = checksheet.id.split('*');
        const key = `${planCode}:${subPlanCode}`;
        lookup.set(key, checksheet);
      });
    return lookup;
  }, [activeChecksheetList, catalogYear]);

  // Optimize search filtering with single pass
  const filteredResults = useMemo(() => {
    if (!searchString) return { programs: [], proposed: [] };

    const searchLower = searchString.toLowerCase();

    // Filter programs with single pass
    const programs = (activeProgramList || []).filter((program) => {
      const key = `${program.acadPlanCode}:${program.subplan?.acadSubPlanCode || 'null'}`;
      if (!checksheetLookup.has(key)) return false;

      return (
        program.acadPlanCode.toLowerCase().includes(searchLower) ||
        program.acadPlanDescription.toLowerCase().includes(searchLower) ||
        (program.subplan &&
          (program.subplan.acadSubPlanCode
            .toLowerCase()
            .includes(searchLower) ||
            program.subplan.description.toLowerCase().includes(searchLower)))
      );
    });

    // Filter proposed with single pass - uses query data matching NewProposedProgramForm structure
    const proposed = (activeProposedList || [])
      .filter((p) => {
        // Match NewProposedProgramForm's logic
        const acadplanMatch = p.checksheetObj?.acadplan
          ?.toLowerCase()
          .includes(searchLower);
        const nameMatch = p.checksheetName?.toLowerCase().includes(searchLower);
        return acadplanMatch || nameMatch;
      })
      .sort((a, b) =>
        (a.checksheetName || '').localeCompare(b.checksheetName || '')
      );

    return {
      programs,
      proposed,
    };
  }, [searchString, activeProgramList, checksheetLookup, activeProposedList]);

  const handleSelectChecksheet = useCallback(
    (program) => {
      const key = `${program.acadPlanCode}:${program.subplan?.acadSubPlanCode || 'null'}`;
      const cs = checksheetLookup.get(key);
      setSelectedProgram(program);
      setSelectedChecksheet(cs);
      setSelectedChecksheetId(cs?.id || null);
    },
    [checksheetLookup]
  );

  const handleSelectProposed = (proposed) => {
    setSelectedProgram('PROPOSED');
    setSelectedChecksheet(proposed);
    setSelectedChecksheetId(proposed?.id || null);
  };

  const handleClearSelectedChecksheet = () => {
    setSearchString('');
    setSelectedChecksheet(null);
    setSelectedChecksheetId(null);
    setSelectedRequirements(new Set()); // Clear selections when changing checksheet
    setSelectedSubsections(new Set()); // Clear subsection selections when changing checksheet
  };

  const handleImport = async () => {
    if (
      !selectedChecksheet ||
      !checksheetDataQuery.data ||
      (selectedRequirements.size === 0 && selectedSubsections.size === 0)
    ) {
      alert('Please select at least one requirement or subsection to import.');
      return;
    }

    try {
      // Parse the source checksheet details
      let sourceDetails = null;
      try {
        sourceDetails =
          typeof checksheetDataQuery.data.details === 'string'
            ? JSON.parse(checksheetDataQuery.data.details)
            : checksheetDataQuery.data.details;
      } catch (e) {
        console.error('Error parsing source checksheet details:', e);
        alert('Error parsing source checksheet data.');
        return;
      }

      if (!sourceDetails?.componentRequirements) {
        alert('No requirements found in source checksheet.');
        return;
      }

      // Extract selected requirements AND track their source subsections
      const requirementsWithSource = []; // {requirement, sourceComponentId, sourceSubsectionId}
      const selectedSubsectionsData = new Map(); // key -> {componentId, subsectionId, subsection data}

      Object.entries(sourceDetails.componentRequirements).forEach(
        ([sourceComponentId, component]) => {
          if (component.subsections) {
            Object.entries(component.subsections).forEach(
              ([sourceSubsectionId, subsection]) => {
                const subsectionKey = `${sourceComponentId}:${sourceSubsectionId}`;

                // Track selected subsections
                if (selectedSubsections.has(subsectionKey)) {
                  selectedSubsectionsData.set(subsectionKey, {
                    sourceComponentId,
                    sourceSubsectionId,
                    subsection: {
                      subsectionName: subsection.subsectionName || '',
                      notes: subsection.notes || '',
                      hours: subsection.hours || 0,
                      upperDivisionHours: subsection.upperDivisionHours || 0,
                    },
                  });
                }

                // Track selected requirements
                if (subsection.requirements) {
                  subsection.requirements.forEach((req) => {
                    if (selectedRequirements.has(req.requirementId)) {
                      requirementsWithSource.push({
                        requirement: req,
                        sourceComponentId,
                        sourceSubsectionId,
                        subsectionKey,
                      });
                    }
                  });
                }
              }
            );
          }
        }
      );

      // Organize requirements by destination:
      // - If subsection is selected, requirements go to new subsection
      // - If subsection not selected, requirements go to target subsection
      const requirementsForTargetSubsection = [];
      const requirementsByNewSubsection = new Map(); // subsectionKey -> [requirements]

      requirementsWithSource.forEach(({ requirement, subsectionKey }) => {
        if (selectedSubsections.has(subsectionKey)) {
          // Subsection is selected, add to new subsection
          if (!requirementsByNewSubsection.has(subsectionKey)) {
            requirementsByNewSubsection.set(subsectionKey, []);
          }
          requirementsByNewSubsection.get(subsectionKey).push(requirement);
        } else {
          // Subsection not selected, add to target subsection
          requirementsForTargetSubsection.push(requirement);
        }
      });

      // Extract course lists from ALL requirements and create new UUIDs for them
      const courseListIdMap = new Map(); // oldId -> newId mapping
      const newCourseLists = {};

      requirementsWithSource.forEach(({ requirement: req }) => {
        if (req.courses && Array.isArray(req.courses)) {
          req.courses.forEach((course) => {
            if (course.itemType === 'req_course_list' && course.courseListId) {
              const oldCourseListId = course.courseListId;

              // If we haven't seen this course list yet, create a new UUID for it
              if (!courseListIdMap.has(oldCourseListId)) {
                const newCourseListId = uuidv4();
                courseListIdMap.set(oldCourseListId, newCourseListId);

                // Copy the course list data from source
                if (
                  sourceDetails.courseLists &&
                  sourceDetails.courseLists[oldCourseListId]
                ) {
                  newCourseLists[newCourseListId] =
                    sourceDetails.courseLists[oldCourseListId];
                }
              }
            }
          });
        }
      });

      // Helper function to update a requirement with new IDs
      // Also filters out template text requirements if templates don't match
      const updateRequirement = (req) => {
        // Check if templates match between source and target
        const templatesMatch =
          details?.templateId === checksheetDataQuery.data?.templateId;

        // Filter out template text from courses (keep custom text, and template text if templates match)
        const filteredCourses = req.courses.filter((course) => {
          if (course.itemType === 'req_text') {
            // Custom text is always importable
            if (course.groupIndex === 'custom_text') {
              return true;
            }
            // Template text is importable only if templates match
            return templatesMatch;
          }
          return true;
        });

        // If all courses were filtered out, skip this requirement
        if (filteredCourses.length === 0) {
          return null;
        }

        const updatedReq = {
          ...req,
          requirementId: uuidv4(), // Generate new UUID for each imported requirement
          courses: filteredCourses,
        };

        if (filteredCourses && Array.isArray(filteredCourses)) {
          updatedReq.courses = filteredCourses.map((course) => {
            if (course.itemType === 'req_course_list' && course.courseListId) {
              const newCourseListId = courseListIdMap.get(course.courseListId);
              if (newCourseListId) {
                return { ...course, courseListId: newCourseListId };
              }
            }
            return course;
          });
        }
        return updatedReq;
      };

      // Update requirements for target subsection (filter out nulls from template text)
      const updatedRequirementsForTarget = requirementsForTargetSubsection
        .map(updateRequirement)
        .filter((req) => req !== null);

      // Update requirements for new subsections (filter out nulls from template text)
      const updatedNewSubsections = new Map();
      requirementsByNewSubsection.forEach((requirements, subsectionKey) => {
        const updatedRequirements = requirements
          .map(updateRequirement)
          .filter((req) => req !== null);
        updatedNewSubsections.set(subsectionKey, updatedRequirements);
      });

      // Create empty new subsections (subsections selected but no requirements)
      selectedSubsectionsData.forEach((subsectionData, subsectionKey) => {
        if (!updatedNewSubsections.has(subsectionKey)) {
          // Empty subsection - no requirements
          updatedNewSubsections.set(subsectionKey, []);
        }
      });

      // Prepare merged course lists
      const mergedCourseLists = {
        ...(courseLists || {}),
        ...newCourseLists,
      };

      // If we're importing course lists OR subsections, do a comprehensive update
      if (
        Object.keys(newCourseLists).length > 0 ||
        updatedNewSubsections.size > 0
      ) {
        // Build updated requirements for the target subsection
        const currentSubsectionRequirements =
          currentRequirements?.[componentId]?.subsections?.[subsectionId]
            ?.requirements || [];

        const updatedSubsectionRequirements = [
          ...currentSubsectionRequirements,
          ...updatedRequirementsForTarget,
        ];

        // Build new subsections with new UUIDs
        const newSubsectionsForComponent = {};
        updatedNewSubsections.forEach((requirements, subsectionKey) => {
          const subsectionData = selectedSubsectionsData.get(subsectionKey);
          const newSubsectionId = uuidv4();

          newSubsectionsForComponent[newSubsectionId] = {
            subsectionName: subsectionData.subsection.subsectionName,
            notes: subsectionData.subsection.notes,
            hours: subsectionData.subsection.hours,
            upperDivisionHours: subsectionData.subsection.upperDivisionHours,
            requirements: requirements,
          };
        });

        // Build complete componentRequirements structure
        const updatedComponentRequirements = {
          ...currentRequirements,
          [componentId]: {
            ...currentRequirements?.[componentId],
            subsections: {
              ...currentRequirements?.[componentId]?.subsections,
              [subsectionId]: {
                ...currentRequirements?.[componentId]?.subsections?.[
                  subsectionId
                ],
                requirements: updatedSubsectionRequirements,
              },
              ...newSubsectionsForComponent, // Add new subsections
            },
          },
        };

        // Remove orphanSection from what we send to API
        const { orphanSection, ...sectionsToSave } =
          updatedComponentRequirements;

        const updatedDetails = {
          ...details,
          componentRequirements: sectionsToSave,
          courseLists: mergedCourseLists,
        };

        try {
          const token = await getAccessToken();
          await checksheetUpdate({
            id: checksheetId,
            jsonData: updatedDetails,
            token,
          });

          // Update Redux state with all three pieces so UI reflects changes immediately
          dispatch(setCourseLists(mergedCourseLists));
          dispatch(setDetails(updatedDetails));
          dispatch(setChecksheetRequirementList(updatedComponentRequirements));
        } catch (error) {
          console.error('🔴 Error importing:', error);
          alert('Failed to import. Please try again.');
          return;
        }
      } else {
        // No course lists or subsections - simple requirement-only import to target subsection
        // Build updated requirements for the target subsection
        const currentSubsectionRequirements =
          currentRequirements?.[componentId]?.subsections?.[subsectionId]
            ?.requirements || [];

        const updatedSubsectionRequirements = [
          ...currentSubsectionRequirements,
          ...updatedRequirementsForTarget,
        ];

        // Build complete componentRequirements structure
        const updatedComponentRequirements = {
          ...currentRequirements,
          [componentId]: {
            ...currentRequirements?.[componentId],
            subsections: {
              ...currentRequirements?.[componentId]?.subsections,
              [subsectionId]: {
                ...currentRequirements?.[componentId]?.subsections?.[
                  subsectionId
                ],
                requirements: updatedSubsectionRequirements,
              },
            },
          },
        };

        // Remove orphanSection from what we send to API
        const { orphanSection, ...sectionsToSave } =
          updatedComponentRequirements;

        const updatedDetails = {
          ...details,
          componentRequirements: sectionsToSave,
        };

        try {
          const token = await getAccessToken();
          await checksheetUpdate({
            id: checksheetId,
            jsonData: updatedDetails,
            token,
          });

          // Update Redux state so UI reflects changes immediately
          dispatch(setDetails(updatedDetails));
          dispatch(setChecksheetRequirementList(updatedComponentRequirements));
        } catch (error) {
          console.error('Error importing requirements:', error);
          alert('Failed to import. Please try again.');
          return;
        }
      }

      const courseListCount = Object.keys(newCourseLists).length;
      const subsectionCount = updatedNewSubsections.size;
      const requirementCount =
        updatedRequirementsForTarget.length +
        Array.from(updatedNewSubsections.values()).reduce(
          (sum, reqs) => sum + reqs.length,
          0
        );

      let message = `Successfully imported ${requirementCount} requirement(s)`;
      if (subsectionCount > 0) {
        message += ` and ${subsectionCount} subsection(s)`;
      }
      if (courseListCount > 0) {
        message += ` and ${courseListCount} course list(s)`;
      }
      message += '!';

      alert(message);
      handleClose();
    } catch (error) {
      alert('Failed to import requirements. Please try again.');
    }
  };

  // Fetch full checksheet data when a checksheet is selected
  const checksheetDataQuery = useQuery({
    queryKey: ['importChecksheet', selectedChecksheetId],
    queryFn: async () => {
      const token = await getAccessToken();
      return checksheetGet({ id: selectedChecksheetId, token });
    },
    enabled: !!selectedChecksheetId,
  });

  useEffect(() => {
    if (selectedProgram && selectedProgram !== 'PROPOSED') {
      const dds = selectedProgram.degreeDescriptionShort
        ? selectedProgram.degreeDescriptionShort === 'CERT'
          ? ', Certificate'
          : `, ${selectedProgram.degreeDescriptionShort}`
        : selectedProgram.acadPlanType === 'MIN'
          ? ', Minor'
          : null;

      setSelectedProgramDegree(dds);
    }
  }, [selectedProgram]);

  // Update dropdown position when search string changes
  useEffect(() => {
    const updatePosition = () => {
      if (searchString && searchInputRef.current) {
        const rect = searchInputRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width,
        });
      } else {
        setDropdownPosition(null);
      }
    };

    updatePosition();

    // Update position on scroll or resize
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [searchString]);

  return (
    <div
      className="d-flex flex-column"
      style={{
        width: '1200px',
        maxHeight: '90vh',
      }}
    >
      <div className="p-6 border-bottom border-gray-3">
        <h2 className="m-0">Import from another checksheet</h2>
      </div>
      <div className="px-6 pt-4" style={{ flex: 1, overflowY: 'auto' }}>
        <div className="w-25 mb-4">
          <label htmlFor="catalog-year-select" className="fw-bold mb-1">
            Catalog year
          </label>
          <select
            id="catalog-year-select"
            aria-label="Catalog year"
            value={catalogYear}
            onChange={(e) => {
              setCatalogYear(e.target.value);
              setSearchString('');
              setSelectedChecksheet(null);
              setSelectedChecksheetId(null);
              setSelectedProgram(null);
            }}
            className="form-select"
            style={{
              color: !catalogYear ? '#6c757d' : 'inherit',
            }}
          >
            <option value="" disabled style={{ color: '#6c757d' }}>
              Select catalog year
            </option>
            {StaticData.yearList.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="w-50 mb-6">
          {!catalogYear ? (
            <div className="text-gray-6" style={{ fontSize: '14px' }}>
              Please select a catalog year to search for checksheets.
            </div>
          ) : !selectedChecksheet ? (
            <>
              <div className="fw-bold lh-1 mb-1">Search checksheets</div>
              <div className="position-relative">
                <input
                  ref={searchInputRef}
                  placeholder="Search by program title or plan code"
                  value={searchString}
                  onChange={(e) => setSearchString(e.target.value)}
                  className="border border-gray-5 ps-2 pe-6 py-1 w-100"
                />
                {searchString &&
                  dropdownPosition &&
                  ReactDOM.createPortal(
                    <div
                      className="border border-top-0 border-gray-5 overflow-y-auto bg-white position-fixed"
                      style={{
                        top: dropdownPosition.top,
                        left: dropdownPosition.left,
                        width: dropdownPosition.width,
                        maxHeight: '198px',
                        zIndex: 9999,
                      }}
                    >
                      {(programsQuery.isLoading ||
                        checksheetsQuery.isLoading ||
                        proposedQuery.isLoading) && (
                        <div className="px-2 py-2 text-gray-6">Loading...</div>
                      )}
                      {!programsQuery.isLoading &&
                        !checksheetsQuery.isLoading &&
                        !proposedQuery.isLoading &&
                        filteredResults.programs.length === 0 &&
                        filteredResults.proposed.length === 0 && (
                          <div className="px-2 py-2 text-gray-6">
                            No checksheets found
                          </div>
                        )}
                      {filteredResults.programs.map((program, index, array) => (
                        <div key={index} className="bg-white px-2">
                          <div
                            className={classNames(
                              index !== array.length - 1 ||
                                filteredResults.proposed.length > 0
                                ? 'border-bottom'
                                : '',
                              'border-gray-5 py-2'
                            )}
                            onClick={() => handleSelectChecksheet(program)}
                            role="button"
                          >
                            <SearchProgram program={program} />
                          </div>
                        </div>
                      ))}
                      {filteredResults.proposed.map(
                        (proposed, index, array) => (
                          <div key={index} className="bg-white px-2">
                            <div
                              className={classNames(
                                index !== array.length - 1 && 'border-bottom',
                                'border-gray-5 py-2'
                              )}
                              onClick={() => handleSelectProposed(proposed)}
                              role="button"
                            >
                              <div className="d-flex gap-2 justify-content-between">
                                <div
                                  className="fw-bold"
                                  style={{ fontSize: '18px' }}
                                >
                                  (PROPOSED) {proposed.checksheetName}
                                </div>
                                <div style={{ marginTop: '3px' }}>
                                  {proposed.checksheetObj.acadplan}
                                </div>
                              </div>
                              <div className="d-flex gap-1 align-items-center">
                                <FontAwesomeIcon
                                  icon={faBuildingColumns}
                                  className="text-gray-6"
                                  width={'16px'}
                                />
                                {proposed?.checksheetObj?.college &&
                                  (proposed.checksheetObj.college === 'OTHER'
                                    ? 'Other'
                                    : !!collegeList?.[
                                        proposed.checksheetObj.college
                                      ] &&
                                      collegeList[
                                        proposed.checksheetObj.college
                                      ].description)}
                              </div>
                            </div>
                          </div>
                        )
                      )}
                    </div>,
                    document.body
                  )}
              </div>
            </>
          ) : selectedProgram === 'PROPOSED' ? (
            <div className="bg-gray-1 border border-gray-3 w-100">
              <div className="p-2">
                <div className="d-flex justify-content-between mb-1">
                  <div className="fw-bold" style={{ fontSize: '18px' }}>
                    {selectedChecksheet.checksheetName}
                  </div>
                  <div className="d-flex gap-3 align-items-center">
                    <div>{selectedChecksheet.checksheetObj.acadplan}</div>
                    <div
                      className="bg-white border border-gray-3 lh-1 rounded-circle"
                      style={{
                        height: 'min-content',
                        padding: '3px 6px 5px',
                      }}
                      onClick={handleClearSelectedChecksheet}
                      role="button"
                    >
                      <FontAwesomeIcon
                        icon={faXmark}
                        style={{ height: '12px', width: '12px' }}
                      />
                    </div>
                  </div>
                </div>
                <div className="d-flex gap-1 align-items-center mb-1">
                  <FontAwesomeIcon
                    icon={faBuildingColumns}
                    className="text-gray-5"
                    width={'16px'}
                  />
                  {selectedChecksheet?.checksheetObj?.college &&
                    (selectedChecksheet.checksheetObj.college === 'OTHER'
                      ? 'Other'
                      : !!collegeList?.[
                          selectedChecksheet.checksheetObj.college
                        ] &&
                        collegeList[selectedChecksheet.checksheetObj.college]
                          .description)}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-1 border border-gray-3 w-100">
              <div className="p-2">
                <div className="d-flex justify-content-between mb-1">
                  <div className="fw-bold" style={{ fontSize: '18px' }}>
                    {selectedProgram.acadPlanDescription}
                    {selectedProgramDegree}
                  </div>
                  <div className="d-flex gap-3 align-items-center">
                    <div>{selectedProgram.acadPlanCode}</div>
                    <div
                      className="bg-white border border-gray-3 lh-1 rounded-circle"
                      style={{
                        height: 'min-content',
                        padding: '3px 6px 5px',
                      }}
                      onClick={handleClearSelectedChecksheet}
                      role="button"
                    >
                      <FontAwesomeIcon
                        icon={faXmark}
                        style={{ height: '12px', width: '12px' }}
                      />
                    </div>
                  </div>
                </div>

                {selectedProgram.subplan && (
                  <div className="d-flex gap-2 justify-content-between">
                    <div className="fw-bold">
                      {selectedProgram.subplan.description}
                      <span className="subplan-tag ms-1">Subplan</span>
                    </div>
                    <div style={{ margin: '3px 50px 0 0' }}>
                      {selectedProgram.subplan.acadSubPlanCode}
                    </div>
                  </div>
                )}

                <div className="d-flex gap-1 align-items-center mb-1">
                  <FontAwesomeIcon
                    icon={faBuildingColumns}
                    className="text-gray-5"
                    width={'16px'}
                  />
                  {selectedProgram.owners.map((owner, index, array) => (
                    <Fragment key={owner.collegeAcadOrg}>
                      {owner.collegeDescription}
                      {index !== array.length - 1 && ', '}
                    </Fragment>
                  ))}
                </div>
                <div className="d-flex gap-1 align-items-center">
                  <FontAwesomeIcon
                    icon={faLocationDot}
                    className="text-gray-5"
                    width={'16px'}
                  />
                  {selectedProgram.campusesOffered.map(
                    (campus, index, array) => (
                      <Fragment key={campus.campusCode}>
                        {
                          StaticData.campusUndergradList.find(
                            (camp) => camp.value === campus.campusCode
                          ).label
                        }
                        {index !== array.length - 1 && ', '}
                      </Fragment>
                    )
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <hr />
        {checksheetDataQuery.isSuccess &&
          checksheetDataQuery.data &&
          (() => {
            const templatesMatch =
              details?.templateId === checksheetDataQuery.data?.templateId;
            return (
              <ChecksheetRequirementsDisplay
                checksheetData={checksheetDataQuery.data}
                selectedRequirements={selectedRequirements}
                setSelectedRequirements={setSelectedRequirements}
                selectedSubsections={selectedSubsections}
                setSelectedSubsections={setSelectedSubsections}
                templatesMatch={templatesMatch}
                willRequirementImport={willRequirementImport}
              />
            );
          })()}
        {checksheetDataQuery.isPending && selectedChecksheetId && (
          <div className="mb-4 text-gray-6">Loading checksheet...</div>
        )}
        {checksheetDataQuery.isError && (
          <div className="mb-4 text-danger">
            Error loading checksheet data: {checksheetDataQuery.error?.message}
          </div>
        )}
      </div>
      <div
        className="p-6 d-flex gap-3 justify-content-between align-items-center"
        style={{ flexShrink: 0, borderTop: '1px solid var(--bs-gray-3)' }}
      >
        <Button
          label={`Import ${selectedRequirements.size + selectedSubsections.size} ${
            selectedRequirements.size + selectedSubsections.size === 1
              ? 'item'
              : 'items'
          }`}
          color="gold"
          onClick={handleImport}
          disabled={
            !selectedChecksheet ||
            (selectedRequirements.size === 0 && selectedSubsections.size === 0)
          }
        />
        <Button label="Cancel" onClick={handleClose} />
      </div>
    </div>
  );
};

export default ImportForm;
